import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="border-b bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <img src="/marketing/logo_official.png" className="h-14" alt="OdontoAssist AI" />
          <Link to="/" className="text-indigo-600 font-semibold flex gap-2">
            <ArrowLeft size={16} /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-10 [&_p]:text-justify [&_li]:text-justify">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="text-indigo-600" size={28} />
            <h1 className="text-4xl font-black">Política de Privacidade</h1>
          </div>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Esta Política de Privacidade descreve como o OdontoAssist AI trata dados pessoais
            no contexto de sua plataforma principal e de recursos complementares, incluindo
            integrações e extensões de navegador, em conformidade com a Lei nº 13.709/2018
            (Lei Geral de Proteção de Dados Pessoais – LGPD).
          </p>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Ao utilizar o OdontoAssist AI, o usuário declara estar ciente de que o tratamento
            de dados ocorrerá nos limites necessários para a prestação do serviço, segurança
            da plataforma, suporte técnico e cumprimento de obrigações legais e regulatórias.
          </p>
        </div>

        <section>
          <h2 className="font-bold text-xl">1. Natureza da Plataforma</h2>
          <p className="leading-relaxed">
            O OdontoAssist AI é um software de gestão odontológica (SaaS), utilizado por
            profissionais e clínicas para organização de dados, prontuários, agenda,
            documentos, anamnese, odontograma, rotinas clínicas e funcionalidades operacionais
            relacionadas ao atendimento odontológico.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">2. Papéis no Tratamento de Dados</h2>
          <p className="leading-relaxed">
            O cliente contratante do sistema, como dentista, clínica ou empresa usuária,
            atua como <strong>Controlador</strong> dos dados pessoais de seus pacientes e
            demais informações inseridas na plataforma. O OdontoAssist AI atua, em regra,
            como <strong>Operador</strong>, tratando dados conforme as instruções e finalidades
            definidas pelo cliente, ressalvadas as hipóteses em que também atue como controlador
            de dados próprios de usuários, administradores, assinantes, visitantes do site
            e contatos institucionais.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">3. Dados Tratados</h2>
          <p className="leading-relaxed">
            Dependendo da funcionalidade utilizada, poderemos tratar as seguintes categorias
            de dados:
          </p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>Dados cadastrais do usuário, clínica ou responsável pela conta, como nome, e-mail e telefone;</li>
            <li>Dados profissionais do usuário e dados operacionais da conta;</li>
            <li>Vínculos com clínicas, papéis, permissões, profissional associado e escopos de visualização de pacientes e agenda;</li>
            <li>Dados cadastrais de pacientes inseridos pelo cliente, como nome, telefone, e-mail e outros campos preenchidos pelo controlador;</li>
            <li>Dados clínicos e informações de saúde inseridos pelo cliente na plataforma, como anamnese, prontuário, evolução clínica, odontograma, documentos e observações;</li>
            <li>Dados de agenda, status de agendamento, informações de atendimento e registros relacionados à rotina da clínica;</li>
            <li>Quando a integração com o Google Calendar for ativada, e-mail da conta Google conectada, identificador da conta, tokens de autorização, identificadores dos eventos e dados necessários à sincronização;</li>
            <li>Logs técnicos, registros de acesso, data e hora de uso, falhas, eventos de segurança e informações necessárias para auditoria e suporte;</li>
            <li>Registros de sessões, dispositivos, autenticação em dois fatores e alertas administrativos de exportação de dados sensíveis;</li>
            <li>Dados operacionais exibidos em integrações e ferramentas complementares, incluindo nome do contato selecionado no WhatsApp Web, telefone quando visível na interface, paciente vinculado, agendamentos relacionados e ações executadas pelo usuário autenticado.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-xl">4. Finalidades do Tratamento</h2>
          <p className="leading-relaxed">
            Os dados tratados pelo OdontoAssist AI são utilizados exclusivamente para finalidades
            legítimas, específicas e compatíveis com a prestação do serviço, tais como:
          </p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>Execução do serviço contratado e disponibilização das funcionalidades da plataforma;</li>
            <li>Gestão de prontuários, agenda, documentos, formulários, registros clínicos e rotinas administrativas;</li>
            <li>Autenticação de usuários, controle de acesso e proteção da conta;</li>
            <li>Segurança da plataforma, prevenção a fraudes, rastreabilidade e integridade operacional;</li>
            <li>Atendimento e suporte técnico ao cliente;</li>
            <li>Melhoria contínua do sistema, correção de falhas e evolução de funcionalidades;</li>
            <li>Operação da extensão OdontoAssist Connect e de integrações relacionadas ao WhatsApp Web, exclusivamente para viabilizar o painel lateral de apoio ao usuário autenticado;</li>
            <li>Sincronização, mediante autorização do usuário, de agendamentos do OdontoAssist AI com o Google Calendar do profissional;</li>
            <li>Cumprimento de obrigação legal, regulatória, contratual ou determinação de autoridade competente.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-xl">5. Extensão OdontoAssist Connect para WhatsApp Web</h2>
          <p className="leading-relaxed">
            O OdontoAssist AI poderá disponibilizar a extensão de navegador
            <strong> OdontoAssist Connect</strong>, compatível com navegadores como
            Google Chrome e Microsoft Edge, destinada exclusivamente a apoiar o uso do
            WhatsApp Web por usuários autenticados da plataforma.
          </p>
          <p className="mt-4 leading-relaxed">
            A extensão atua como um <strong>painel lateral de apoio</strong> e pode:
          </p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>Identificar o contato atualmente selecionado no WhatsApp Web, com base em elementos visíveis da interface;</li>
            <li>Consultar, junto à API segura do OdontoAssist AI, dados do paciente correspondente, agendamentos e ações autorizadas do usuário autenticado;</li>
            <li>Permitir acesso rápido ao prontuário, à agenda e a mensagens de apoio operacional;</li>
            <li>Permitir a criação, atualização ou remarcação de agendamentos, quando tais ações forem iniciadas manualmente pelo usuário.</li>
          </ul>
          <p className="mt-4 leading-relaxed">
            A extensão <strong>não automatiza o envio de mensagens</strong>, não envia conteúdo
            em nome do usuário sem sua ação direta, não monitora o histórico geral de navegação
            e não realiza coleta irrestrita de dados fora do contexto necessário para o
            funcionamento do painel lateral no WhatsApp Web e para a comunicação com o domínio
            oficial do OdontoAssist AI.
          </p>
          <p className="mt-4 leading-relaxed">
            Os dados acessados pela extensão são utilizados exclusivamente para viabilizar a
            funcionalidade contratada, melhorar a experiência operacional do usuário e permitir
            o correto funcionamento do painel de apoio. A extensão não vende dados pessoais,
            não utiliza dados para publicidade comportamental e não compartilha informações além
            do necessário para execução do serviço, obrigação legal ou requisição de autoridade
            competente.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">6. Integração com o Google Calendar</h2>
          <p className="leading-relaxed">
            A integração com o Google Calendar é opcional e somente é ativada após autorização
            expressa do usuário na tela de consentimento do Google. O OdontoAssist AI solicita
            acesso para criar, consultar, alterar e excluir eventos em calendários pertencentes
            à conta Google conectada, na medida necessária para manter os agendamentos sincronizados.
          </p>
          <p className="mt-4 leading-relaxed">
            No funcionamento atual, a sincronização ocorre do OdontoAssist AI para o Google Calendar.
            A plataforma não importa compromissos pessoais do Google para o OdontoAssist AI. São enviados
            ao Google os dados necessários para formar o evento, como data, horário, duração, procedimento,
            profissional e identificação reduzida do paciente, composta pelo primeiro nome e pela inicial
            do último sobrenome. Identificadores técnicos internos do agendamento, do profissional e da conta
            também podem ser incluídos nas propriedades privadas do evento para manter o vínculo entre os
            sistemas. Observações clínicas não são enviadas, salvo se uma configuração específica vier a ser
            habilitada e devidamente informada ao usuário.
          </p>
          <p className="mt-4 leading-relaxed">
            Para manter o vínculo entre os sistemas, poderão ser armazenados o e-mail e o identificador da
            conta Google, tokens OAuth criptografados, identificadores dos eventos, estado da sincronização,
            registros de falha e datas de processamento. O OdontoAssist AI não recebe nem armazena a senha
            da conta Google.
          </p>
          <p className="mt-4 leading-relaxed">
            Os dados obtidos por meio das APIs do Google são utilizados exclusivamente para fornecer e
            proteger a funcionalidade solicitada. Eles não são vendidos, utilizados para publicidade,
            criação de perfis comerciais ou treinamento de modelos de inteligência artificial. O uso e a
            transferência dessas informações observam a
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer"
              className="text-indigo-600 font-semibold hover:underline"
            > Política de Dados do Usuário dos Serviços de API do Google</a>, incluindo os requisitos de Uso Limitado.
          </p>
          <p className="mt-4 leading-relaxed">
            O usuário pode interromper a integração em Configurações → Integrações, optando por manter no
            Google os eventos já criados ou solicitar sua remoção antes da desconexão. A autorização também
            pode ser revogada nas configurações de segurança da própria Conta Google. Após a desconexão, os
            tokens deixam de ser utilizados e são removidos ou inutilizados, ressalvados registros técnicos
            e obrigações legais aplicáveis.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">7. Compartilhamento de Dados</h2>
          <p className="leading-relaxed">
            O OdontoAssist AI não comercializa dados pessoais. O compartilhamento poderá ocorrer
            apenas quando necessário e proporcional, nas seguintes hipóteses:
          </p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>Prestadores de infraestrutura tecnológica, hospedagem, segurança e suporte operacional;</li>
            <li>Fornecedores estritamente necessários à execução de funcionalidades contratadas;</li>
            <li>Cumprimento de obrigação legal, regulatória ou ordem de autoridade competente;</li>
            <li>Proteção de direitos, segurança da plataforma, prevenção a fraudes ou resposta a incidentes.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-xl">8. Segurança da Informação</h2>
          <p className="leading-relaxed">
            Adotamos medidas técnicas e organizacionais adequadas para proteção dos dados pessoais
            contra acesso não autorizado, destruição, perda, alteração, vazamento ou qualquer forma
            de tratamento inadequado ou ilícito, observando padrões compatíveis com a natureza da
            plataforma e os riscos envolvidos.
          </p>
          <p className="mt-4 leading-relaxed">
            Embora empreguemos boas práticas de segurança, nenhum sistema é absolutamente imune a riscos.
            Por isso, recomendamos que os usuários também adotem medidas adequadas de proteção de conta,
            credenciais e dispositivos utilizados para acesso ao sistema.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">9. Bases Legais</h2>
          <p className="leading-relaxed">
            O tratamento de dados poderá ocorrer com fundamento, conforme o caso, nas bases legais
            previstas na LGPD, incluindo execução de contrato, cumprimento de obrigação legal ou
            regulatória, exercício regular de direitos, legítimo interesse e tutela da saúde, quando
            aplicável ao contexto da atuação do cliente controlador e das funcionalidades da plataforma.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">10. Direitos dos Titulares</h2>
          <p className="leading-relaxed">
            Os titulares dos dados pessoais poderão exercer os direitos previstos na LGPD, como acesso,
            correção, eliminação, anonimização, portabilidade, informação sobre compartilhamento e revisão
            de decisões automatizadas, conforme aplicável.
          </p>
          <p className="mt-4 leading-relaxed">
            Quando os dados tiverem sido inseridos na plataforma por clínica, dentista ou cliente contratante,
            o exercício dos direitos deverá ser solicitado prioritariamente ao respectivo <strong>Controlador</strong>.
            O OdontoAssist AI poderá cooperar com o controlador para viabilizar o atendimento dessas solicitações,
            nos limites de sua atuação como operador.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">11. Retenção e Exclusão</h2>
          <p className="leading-relaxed">
            Os dados serão mantidos pelo tempo necessário ao cumprimento das finalidades descritas nesta Política,
            à execução do contrato, à observância de obrigações legais e regulatórias e à preservação de direitos.
            Encerrado o período aplicável, os dados poderão ser eliminados, anonimizados ou mantidos de forma
            segura quando houver fundamento legal para sua conservação.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">12. Atualizações desta Política</h2>
          <p className="leading-relaxed">
            Esta Política de Privacidade poderá ser atualizada a qualquer momento para refletir alterações
            legais, regulatórias, operacionais, técnicas ou de funcionalidades da plataforma e de seus
            recursos complementares, incluindo integrações e extensões. Recomendamos consulta periódica
            desta página.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-xl">13. Contato</h2>
          <p className="leading-relaxed">
            Em caso de dúvidas sobre esta Política de Privacidade, tratamento de dados ou solicitações
            relacionadas ao OdontoAssist AI, o contato poderá ser realizado pelo e-mail
            <strong> suporte@odontoassistai.com.br</strong> ou pelos demais canais oficiais
            disponibilizados no site da plataforma. O OdontoAssist AI é operado pela
            <strong> Serrano Digital Solutions Ltda.</strong>
          </p>
          <p className="mt-4 leading-relaxed">
            Cada colaborador deve utilizar credencial individual. Administradores da clínica podem definir
            papéis, permissões e escopos, consultar eventos de auditoria, revisar exportações de prontuários,
            suspender acessos e encerrar sessões. A autenticação em dois fatores pode ser ativada pelo usuário.
          </p>
        </section>

        <section className="text-sm text-gray-500 border-t pt-6">
          <p>Última atualização: 8 de agosto de 2026.</p>
        </section>
      </main>
    </div>
  );
}
